import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Tooltip } from '@neo/primitives/tooltip'
import { downloadFile } from '@shared/utils'
import { DownloadIcon, Loader2, PauseIcon, PlayIcon } from 'lucide-react'
import type React from 'react'
import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { AudioPlayerProps } from './types'

const speedOptions = [
  { value: '0.75', label: '0.75x' },
  { value: '1', label: '1x' },
  { value: '1.5', label: '1.5x' },
  { value: '2', label: '2x' },
]

export const AudioPlayer = ({
  url,
  fileName,
  className,
  loading,
  audioPlayerRef,
}: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLButtonElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [speed, setSpeed] = useState('1')
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const setAudioData = () => setDuration(audio.duration)
    const setAudioTime = () => setCurrentTime(audio.currentTime)
    const setAudioEnded = () => setIsPlaying(false)

    audio.addEventListener('loadeddata', setAudioData)
    audio.addEventListener('timeupdate', setAudioTime)
    audio.addEventListener('ended', setAudioEnded)

    return () => {
      audio.removeEventListener('loadeddata', setAudioData)
      audio.removeEventListener('timeupdate', setAudioTime)
      audio.removeEventListener('ended', setAudioEnded)
    }
  }, [])

  useImperativeHandle(audioPlayerRef, () => ({
    play: () => audioRef.current?.play() && setIsPlaying(true),
    pause: () => audioRef.current?.pause() && setIsPlaying(false),
    isPlaying: () => isPlaying,
  }))

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.pause()
      else audioRef.current.play()

      setIsPlaying(!isPlaying)
    }
  }

  const changeSpeed = (newSpeed: string) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = parseFloat(newSpeed)
      setSpeed(newSpeed)
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (progressRef.current && audioRef.current) {
      const rect = progressRef.current.getBoundingClientRect()
      const percent = (e.clientX - rect.left) / rect.width
      audioRef.current.currentTime = percent * duration
    }
  }

  return (
    <div className={cn('flex flex-col gap-2 overflow-hidden', className)}>
      <div className="flex flex-wrap items-center gap-2 rounded-lg w-full bg-background p-2">
        <audio ref={audioRef} src={url}>
          <track kind="captions" />
        </audio>
        {loading ? (
          <Loader2 className="animate-spin h-6 w-6" />
        ) : (
          <Tooltip content={isPlaying ? 'Pause' : 'Play'}>
            <button
              type="button"
              onClick={togglePlay}
              className="shrink-0 p-2 text-primary-foreground bg-primary rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              data-testid="button-media-play-pause"
              disabled={loading}
            >
              {isPlaying ? (
                <PauseIcon size={16} className="fill-current" />
              ) : (
                <PlayIcon size={16} className="fill-current" />
              )}
            </button>
          </Tooltip>
        )}
        <div className="font-jetbrains text-xs-400 text-secondary tabular-nums whitespace-nowrap">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        <div
          className="min-w-8 max-w-72 flex-1 relative h-2 bg-muted cursor-pointer rounded-full overflow-hidden focus-visible:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
          role="slider"
          aria-label="Audio progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          data-testid="slider-media-progress"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              if (audioRef.current) audioRef.current.currentTime += 5
            } else if (e.key === 'ArrowLeft') {
              if (audioRef.current) audioRef.current.currentTime -= 5
            }
          }}
        >
          <button
            type="button"
            ref={progressRef}
            className="absolute inset-0"
            onClick={handleProgressClick}
            aria-label="Audio progress"
            disabled={loading}
          />
          <div
            className="h-full bg-primary pointer-events-none"
            style={{ width: `${progress}%` }}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex shrink-0 items-center h-7 min-w-10 text-center gap-1 p-1 bg-hv1 text-secondary rounded-lg justify-center"
            asChild
          >
            <Button
              className="font-jetbrains text-xs-400 tabular-nums tracking-normal normal-case"
              disabled={loading}
              variant="ghost"
              aria-label="Playback speed"
              data-testid="button-media-playback-speed"
            >
              {speed}x
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {speedOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onSelect={() => changeSpeed(option.value)}
                className={cn(
                  'font-jetbrains text-xs-400 tabular-nums',
                  speed === option.value && 'bg-accent',
                )}
                data-testid={`option-media-speed-${option.value}`}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip content="Download">
          <Button
            type="button"
            className="shrink-0 p-1 bg-hv1 text-secondary rounded-lg h-7 w-7"
            onClick={() => downloadFile(url, fileName)}
            data-action="download"
            aria-label={fileName ? `Download ${fileName}` : 'Download audio'}
            data-testid="button-download-audio"
            disabled={loading}
            variant="ghost"
            size="iconSm"
          >
            <DownloadIcon size={16} />
          </Button>
        </Tooltip>
      </div>
      {fileName && (
        <span className="font-jetbrains text-xs-400 text-secondary text-left break-all">
          {fileName}
        </span>
      )}
    </div>
  )
}
